import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  FaUsers,
  FaUserCheck,
  FaUserTimes,
  FaClock,
  FaBriefcase,
  FaBuilding,
  FaTasks,
  FaChartLine,
  FaHandshake,
} from "react-icons/fa";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  Tooltip,
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  ComposedChart,
  YAxis,
  PieChart ,
  Pie ,
  Cell ,
} from "recharts";

import { getData, postData } from "../../../services/FetchNodeservices";

export default function Core() {

  // =====================================================
  // STATES
  // =====================================================

  const [loading, setLoading] = useState(true);

const [dashboard, setDashboard] =
  useState({

    totalEmployees: 0,

    Online: 0,

    Offline: 0,

    lateLogin: 0,

    activeVendors: 0,

    workingTasks: 0,

    activeJobs: 0,

    closedPositions: 0,

    candidates: 0,

    totalRevenue: 0,
});

  const [attendanceAnalytics, setAttendanceAnalytics] = useState([]);

  const [recruiterPerformance, setRecruiterPerformance] =
    useState([]);

  const [revenueData, setRevenueData] = useState([]);

  const [hiringFlow, setHiringFlow] = useState([]);

  const [monthlyHiring, setMonthlyHiring] = useState([]);
  const [taskData, setTaskData] = useState([]);
  const [currentMonthMatrix,
setCurrentMonthMatrix] =
useState([]);

  const [
  closureRevenue,
  setClosureRevenue,
] = useState([]);

  const [clientRevenue, setClientRevenue] = useState([]);
  const [recruiterLeaders, setRecruiterLeaders] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // =====================================================
  // LOAD DASHBOARD
  // =====================================================

  useEffect(() => {
    loadDashboard();
  }, []);
const navigate = useNavigate();

const loadDashboard = async () => {

  try {

    setLoading(true);

    // =================================================
    // HELPERS
    // =================================================

    const safePost = async (url, body = {}) => {

      try {

        return await postData(url, body);

      } catch (e) {

        console.log(e);

        return {
          status: false,
          data: [],
        };
      }
    };

    const safeGet = async (url) => {

      try {

        return await getData(url);

      } catch (e) {

        console.log(e);

        return {
          status: false,
          data: [],
        };
      }
    };

    const parseCustomDate = (str) => {
      if (!str) return new Date("");
      if (str instanceof Date) return str;
      // Handle DD-MM-YYYY format
      if (typeof str === "string") {
        const parts = str.split("-");
        if (parts.length === 3 && parts[2].length === 4) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          return new Date(year, month, day);
        }
      }
      return new Date(str);
    };

    const formatDate = (date) => {
      if (!date) return "";
      const d = parseCustomDate(date);
      if (isNaN(d)) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    // =================================================
    // FETCH ALL
    // =================================================

    const [
      employees,
      candidates,
      attendance,
      closures,
      positionMatrix,
      jobs,
      vendors,
      tasks,
    ] = await Promise.all([

      safePost("users/fetch"),

      safeGet("candidate"),

      safeGet("adminattendance/view"),

      safeGet("clouserreport/fetch"),

      safeGet("positionmatrix/fetch"),

      safeGet("job/jobs"),

      safeGet("vendor/all"),

      safePost("workingtask/fetch"),
    ]);

    // =================================================
    // NORMALIZE
    // =================================================

    const employeeRows = Array.isArray(employees?.data)
      ? employees.data
      : [];

    const candidateRows = Array.isArray(candidates?.data)
      ? candidates.data
      : [];

    const attendanceRows = Array.isArray(attendance?.data)
      ? attendance.data
      : [];

    const closureRows = Array.isArray(closures?.data)
      ? closures.data
      : [];

    const matrixRows = Array.isArray(positionMatrix?.data)
      ? positionMatrix.data
      : [];

    const vendorRows = Array.isArray(vendors?.data)
      ? vendors.data
      : [];

    const taskRows = Array.isArray(tasks?.data)
      ? tasks.data
      : [];

    const jobRows = Array.isArray(jobs?.jobs)
      ? jobs.jobs
      : [];

    setTaskData(taskRows);

    // =================================================
    // DATE
    // =================================================

    const now = new Date();

    // Dynamically align currentMonth and currentYear to latest database records
    let currentMonth = now.getMonth();
    let currentYear = now.getFullYear();

    let latestDate = null;
    closureRows.forEach((item) => {
      const d = parseCustomDate(item.doj || item.created_at);
      if (!isNaN(d)) {
        if (!latestDate || d > latestDate) {
          latestDate = d;
        }
      }
    });

    if (latestDate) {
      currentYear = latestDate.getFullYear();
      currentMonth = latestDate.getMonth();
    }
    setCurrentYear(currentYear);

    // Align today to the latest attendance record date so online/offline stats show data
    let latestAttendanceDate = null;
    attendanceRows.forEach((item) => {
      const d = parseCustomDate(item.date || item.created_at);
      if (!isNaN(d)) {
        if (!latestAttendanceDate || d > latestAttendanceDate) {
          latestAttendanceDate = d;
        }
      }
    });

    const todayDate = latestAttendanceDate || now;
    const today = formatDate(todayDate);

    // =================================================
    // TODAY CANDIDATES
    // =================================================

    const todayCandidates = candidateRows.filter((item) => {

      return (
        formatDate(
          item.uploaded_on ||
          item.created_at
        ) === today
      );
    });

    // =================================================
    // TODAY ATTENDANCE
    // =================================================

    const todayAttendance = attendanceRows.filter((item) => {

      return (
        formatDate(
          item.date ||
          item.created_at
        ) === today
      );
    });

    // =================================================
    // UNIQUE EMPLOYEE ATTENDANCE
    // =================================================

    const attendanceMap = {};

    todayAttendance.forEach((item) => {

      const code = item.employee_code;

      if (!code) return;

      if (!attendanceMap[code]) {

        attendanceMap[code] = item;

      } else {

        const oldTime = new Date(
          attendanceMap[code].punch_in_time ||
          attendanceMap[code].created_at
        ).getTime();

        const newTime = new Date(
          item.punch_in_time ||
          item.created_at
        ).getTime();

        if (newTime > oldTime) {

          attendanceMap[code] = item;
        }
      }
    });

    const uniqueAttendance =
      Object.values(attendanceMap);

    // =================================================
    // ONLINE / OFFLINE
    // =================================================

    const totalOnline = uniqueAttendance.filter(
      (item) =>
        item.punch_in_time &&
        !item.punch_out_time
    ).length;

    const totalOffline = Math.max(
      employeeRows.length - totalOnline,
      0
    );

    // =================================================
    // LATE LOGIN
    // =================================================

    const totalLate = uniqueAttendance.filter((item) => {

      if (!item.punch_in_time) return false;

      const inTime = new Date(item.punch_in_time);

      return (
        inTime.getHours() > 9 ||
        (
          inTime.getHours() === 9 &&
          inTime.getMinutes() > 30
        )
      );
    }).length;

    // =================================================
    // ACTIVE JOBS
    // =================================================

    const activeJobs = jobRows.filter((item) => {

      return (
        String(
          item.status || ""
        ).toLowerCase() === "active"
      );
    }).length;

    // =================================================
    // TODAY'S POSITIONS
    // =================================================

    const todaysPositions = jobRows.filter((item) => {

      return (
        formatDate(
          item.created_at ||
          item.created_on ||
          item.open_date
        ) === today
      );
    }).length;

    // =================================================
    // CLOSED POSITIONS
    // =================================================

    const closedJobs = closureRows.filter((item) => {

      return (
        String(
          item.status_jo || ""
        ).toLowerCase() === "joined"
      );
    }).length;

    // =================================================
    // TODAY JOININGS
    // =================================================

    const todaysJoining = closureRows.filter((item) => {

      return (
        formatDate(item.doj) === today
      );
    }).length;

    // =================================================
    // MONTH REVENUE
    // =================================================

    const monthlyClosures = closureRows.filter((item) => {

      const d = parseCustomDate(
        item.doj ||
        item.created_at
      );

      if (isNaN(d)) return false;

      return (
        d.getMonth() === currentMonth &&
        d.getFullYear() === currentYear &&
        String(
          item.status_jo || ""
        ).toLowerCase() === "joined"
      );
    });

    const totalRevenue = monthlyClosures.reduce(
      (sum, item) => {

        const amount = parseFloat(
          item.amount ||
          item.salary ||
          0
        );

        return sum + (
          isNaN(amount)
            ? 0
            : amount
        );
      },
      0
    );

    // =================================================
    // CURRENT MONTH MATRIX
    // =================================================

    const currentMonthMatrixData = matrixRows.filter((item) => {

      const d = parseCustomDate(
        item.position_received_on
      );

      if (isNaN(d)) return false;

      return (
        d.getMonth() === currentMonth &&
        d.getFullYear() === currentYear
      );
    });

    setCurrentMonthMatrix(
      currentMonthMatrixData
    );

    // =================================================
    // INTERVIEW COUNTS
    // =================================================

    const interviewL1 = currentMonthMatrixData.reduce(
      (sum, item) =>
        sum + Number(item["Interview L1"] || 0),
      0
    );

    const interviewL2 = currentMonthMatrixData.reduce(
      (sum, item) =>
        sum + Number(item["Interview L2"] || 0),
      0
    );

    const faceToFace = currentMonthMatrixData.reduce(
      (sum, item) =>
        sum + Number(item["Face to Face"] || 0),
      0
    );

    // =================================================
    // YEARLY ANALYTICS
    // =================================================

    const months = [
      "Jan","Feb","Mar","Apr","May","Jun",
      "Jul","Aug","Sep","Oct","Nov","Dec"
    ];

    const yearlyData = months.map((m) => ({
      month: m,
      closures: 0,
      activePositions: 0,
    }));

    closureRows.forEach((item) => {

      const d = parseCustomDate(
        item.doj ||
        item.created_at
      );

      if (isNaN(d)) return;

      if (d.getFullYear() !== currentYear) return;

      yearlyData[d.getMonth()].closures += 1;
    });

    jobRows.forEach((item) => {

      const d = parseCustomDate(
        item.created_at ||
        item.created_on
      );

      if (isNaN(d)) return;

      if (d.getFullYear() !== currentYear) return;

      yearlyData[d.getMonth()].activePositions += 1;
    });

    setRecruiterPerformance(yearlyData);

    // =================================================
    // HIRING FLOW
    // =================================================

    const hiringFlowData = months.map((m) => ({
      month: m,
      interview: 0,
      joining: 0,
      closure: 0,
    }));

    currentMonthMatrixData.forEach((item) => {

      const d = parseCustomDate(
        item.position_received_on
      );

      if (isNaN(d)) return;

      const idx = d.getMonth();

      hiringFlowData[idx].interview +=
        Number(item["Interview L1"] || 0) +
        Number(item["Interview L2"] || 0);

      hiringFlowData[idx].joining +=
        Number(item["Joined"] || 0);

      hiringFlowData[idx].closure +=
        Number(item["Selected"] || 0);
    });

    setHiringFlow(hiringFlowData);

    // =================================================
    // MONTHLY HIRING
    // =================================================

    const monthlyHiringData = months.map((m) => ({
      month: m,
      hiring: 0,
    }));

    closureRows.forEach((item) => {
      const d = parseCustomDate(item.doj || item.created_at);
      if (isNaN(d)) return;
      if (d.getFullYear() === currentYear && String(item.status_jo || "").toLowerCase() === "joined") {
        monthlyHiringData[d.getMonth()].hiring += 1;
      }
    });

    setMonthlyHiring(monthlyHiringData);

    // =================================================
    // CLIENT REVENUE SHARE
    // =================================================
    const clientRevenueMap = {};
    closureRows.forEach((item) => {
      if (String(item.status_jo || "").toLowerCase() === "joined") {
        const comp = item.company || "Unknown";
        const amt = parseFloat(item.amount || 0);
        if (!isNaN(amt)) {
          clientRevenueMap[comp] = (clientRevenueMap[comp] || 0) + amt;
        }
      }
    });
    const clientRevenueList = Object.entries(clientRevenueMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    const topClients = clientRevenueList.slice(0, 5);
    const otherClientsValue = clientRevenueList.slice(5).reduce((sum, item) => sum + item.value, 0);
    if (otherClientsValue > 0) {
      topClients.push({ name: "Others", value: otherClientsValue });
    }
    setClientRevenue(topClients);

    // =================================================
    // RECRUITER PERFORMANCE LEADERBOARD
    // =================================================
    const recruiterMap = {};
    closureRows.forEach((item) => {
      const name = item.associate_name || "Unknown";
      if (!recruiterMap[name]) {
        recruiterMap[name] = { name, closures: 0, revenue: 0 };
      }
      if (String(item.status_jo || "").toLowerCase() === "joined") {
        recruiterMap[name].closures += 1;
        const amt = parseFloat(item.amount || 0);
        if (!isNaN(amt)) {
          recruiterMap[name].revenue += amt;
        }
      }
    });
    const recruiterLeaderboard = Object.values(recruiterMap)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
    setRecruiterLeaders(recruiterLeaderboard);

    // =================================================
    // ATTENDANCE ANALYTICS
    // =================================================

    const attendanceAnalyticsData = [
      "Mon","Tue","Wed","Thu","Fri","Sat"
    ].map((d) => ({
      day: d,
      Online: 0,
      Offline: 0,
      late: 0,
      hours: 0,
      punchOutCount: 0,
    }));

    attendanceRows.forEach((item) => {

      if (!item.date) return;

      const d = parseCustomDate(item.date);

      if (isNaN(d)) return;

      const idx = d.getDay();

      if (idx === 0 || idx > 6) return;

      const row =
        attendanceAnalyticsData[idx - 1];

      if (
        item.punch_in_time &&
        !item.punch_out_time
      ) {
        row.Online += 1;
      }

      if (item.punch_out_time) {
        row.Offline += 1;
      }

      if (item.punch_in_time) {

        const t = parseCustomDate(
          item.punch_in_time
        );

        if (
          t.getHours() > 9 ||
          (
            t.getHours() === 9 &&
            t.getMinutes() > 30
          )
        ) {
          row.late += 1;
        }
      }

      if (
        item.punch_in_time &&
        item.punch_out_time
      ) {

        const inTime = parseCustomDate(
          item.punch_in_time
        );

        const outTime = parseCustomDate(
          item.punch_out_time
        );

        const hrs =
          (outTime - inTime) /
          (1000 * 60 * 60);

        row.hours += hrs;
        row.punchOutCount += 1;
      }
    });

    attendanceAnalyticsData.forEach((row) => {
      if (row.punchOutCount > 0) {
        row.hours = Number((row.hours / row.punchOutCount).toFixed(1));
      } else {
        row.hours = 0;
      }
    });

    setAttendanceAnalytics(
      attendanceAnalyticsData
    );

    // =================================================
    // FINAL DASHBOARD
    // =================================================

    setDashboard({

      totalEmployees:
        employeeRows.length,

      Online:
        totalOnline,

      Offline:
        totalOffline,

      lateLogin:
        totalLate,

      activeVendors:
        vendorRows.filter(
          (item) =>
            String(
              item.status || ""
            ).toLowerCase() === "active"
        ).length,

      workingTasks:
        taskRows.length,

      activeJobs:
        todaysPositions,

      totalActiveJobs:
        activeJobs,

      closedPositions:
        closedJobs,

      candidates:
        candidateRows.length,

      todayCandidates:
        todayCandidates.length,

      totalRevenue,

      interviewL1,

      interviewL2,

      faceToFace,

      joinedCount:
        monthlyClosures.length,

      todaysJoining,
    });

  } catch (err) {

    console.log(err);

    alert("Dashboard Load Failed");

  } finally {

    setLoading(false);
  }
};
// =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center">
        <div className="text-center">
          <div
            className="
              h-14
              w-14
              border-4
              border-black
              border-t-transparent
              rounded-full
              animate-spin
              mx-auto
            "
          />

          <p className="mt-5 text-gray-600">
            Loading Dashboard...
          </p>
        </div>
      </div>
    );
  }

  // =====================================================
  // KPI CARDS
  // =====================================================

const cards = [
  // =================================================
  // TODAY'S POSITIONS
  // =================================================
  {
    title: "Today's Positions",
    value: Number(dashboard.activeJobs || 0).toLocaleString(),
    icon: <FaBriefcase />,
    path: "/jobs-report?filter=today",
    color: "from-blue-500 to-cyan-400",
  },

  // =================================================
  // TOTAL ACTIVE POSITIONS
  // =================================================
  {
    title: "Active Positions",
    value: Number(dashboard.totalActiveJobs || 0).toLocaleString(),
    icon: <FaTasks />,
    path: "/jobs-report?status=active",
    color: "from-indigo-500 to-purple-400",
  },

  // =================================================
  // CLOSED POSITIONS
  // =================================================
  {
    title: "Closed Positions",
    value: Number(dashboard.closedPositions || 0).toLocaleString(),
    icon: <FaChartLine />,
    path: "/jobs-report?status=closed",
    color: "from-emerald-500 to-teal-400",
  },

  // =================================================
  // MONTHLY REVENUE
  // =================================================
  {
    title: "Revenue This Month",
    value: `₹${Number(dashboard.totalRevenue || 0).toLocaleString("en-IN")}`,
    icon: <FaBuilding />,
    path: "/closure-report?filter=currentMonth",
    color: "from-amber-500 to-orange-400",
  },

  // =================================================
  // TODAY CANDIDATES
  // =================================================
  {
    title: "Today's Candidates",
    value: Number(dashboard.todayCandidates || 0).toLocaleString(),
    icon: <FaUsers />,
    path: "/candidate-report?filter=today",
    color: "from-pink-500 to-rose-400",
  },

  // =================================================
  // INTERVIEW LINEUP
  // =================================================
  {
    title: "Interview Line Up",
    value: Number(
      (Number(dashboard.interviewL1 || 0) +
        Number(dashboard.interviewL2 || 0) +
        Number(dashboard.faceToFace || 0)) || 0
    ).toLocaleString(),
    icon: <FaUserCheck />,
    path: "/interview-report?filter=currentMonth",
    color: "from-violet-500 to-fuchsia-400",
  },

  // =================================================
  // TODAY JOINING
  // =================================================
  {
    title: "Today's Joining",
    value: Number(dashboard.todaysJoining || 0).toLocaleString(),
    icon: <FaClock />,
    path: "/joining-report?filter=today",
    color: "from-sky-500 to-indigo-400",
  },

  // =================================================
  // ACTIVE VENDORS
  // =================================================
  {
    title: "Active Vendors",
    value: Number(dashboard.activeVendors || 0).toLocaleString(),
    icon: <FaHandshake />,
    path: "/vendor",
    color: "from-teal-500 to-emerald-400",
  },
];

  // =====================================================
  // RETURN
  // =====================================================

  return (
    <div className="md:ml-72 px-4 md:px-6 pt-24 pb-8 min-h-screen text-slate-800">

{/* ================================================= */}
{/* KPI CARDS */}
{/* ================================================= */}

{/* ================================================= */}
{/* KPI CARDS */}
{/* ================================================= */}

<div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
  {cards.map((item, index) => (
    <div
      key={index}
      onClick={() => navigate(item.path)}
      className="
        group
        glass-card
        p-4
        cursor-pointer
        hover:-translate-y-1
      "
    >
      {/* TOP */}
      <div className="flex items-center justify-between">
        <div
          className={`
            w-9
            h-9
            rounded-xl
            bg-gradient-to-br
            ${item.color || "from-indigo-500 to-purple-500"}
            text-white
            flex
            items-center
            justify-center
            text-sm
            shadow-sm
          `}
        >
          {item.icon}
        </div>

        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-[9px] font-bold text-slate-500 uppercase tracking-wider group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
          View
        </span>
      </div>

      {/* VALUE */}
      <h2 className="text-lg font-bold text-slate-800 mt-3.5 tracking-tight truncate">
        {item.value}
      </h2>

      {/* TITLE */}
      <p className="text-[10px] text-slate-500 mt-1 font-medium leading-normal">
        {item.title}
      </p>
    </div>
  ))}
</div>

   {/* ================================================= */}
{/* ACTIVITY ANALYTICS */}
{/* ================================================= */}
{/* ================================================= */}
{/* ROW 1: ATTENDANCE & CLIENT REVENUE SHARE */}
{/* ================================================= */}
<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

  {/* Current Week Attendance */}
  <div className="glass-card p-5 flex flex-col justify-between">
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Attendance Analytics</h2>
          <p className="text-xs text-slate-500 mt-1">Current Week Attendance (Mon - Sat)</p>
        </div>
        <button
          onClick={() => navigate("/adminattendance")}
          className="bg-indigo-600 text-white px-3.5 py-1.5 rounded-xl text-xs font-semibold hover:bg-indigo-755 transition-colors shadow-sm"
        >
          View Attendance
        </button>
      </div>

      {/* KPI Stats inside attendance card */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100/60 shadow-xs">
          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Online</p>
          <h2 className="text-xl font-bold mt-1 text-indigo-600">{dashboard.Online || 0}</h2>
        </div>
        <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100/60 shadow-xs">
          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Offline</p>
          <h2 className="text-xl font-bold mt-1 text-slate-700">{dashboard.Offline || 0}</h2>
        </div>
        <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100/60 shadow-xs">
          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Late</p>
          <h2 className="text-xl font-bold mt-1 text-rose-500">{dashboard.lateLogin || 0}</h2>
        </div>
        <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100/60 shadow-xs">
          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Avg Hrs</p>
          <h2 className="text-xl font-bold mt-1 text-amber-600">
            {attendanceAnalytics.length > 0
              ? (attendanceAnalytics.reduce((sum, item) => sum + item.hours, 0) / attendanceAnalytics.length).toFixed(1)
              : "0"
            }h
          </h2>
        </div>
      </div>
    </div>

    <div className="h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={attendanceAnalytics} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
          <CartesianGrid stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 11 }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 10 }} />
          <Tooltip
            cursor={{ fill: "#f8fafc" }}
            contentStyle={{ borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.05)" }}
          />
          <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "10px" }} />
          <Bar dataKey="Online" fill="#4f46e5" radius={[4, 4, 0, 0]} barSize={16} name="Online" />
          <Bar dataKey="Offline" fill="#cbd5e1" radius={[4, 4, 0, 0]} barSize={16} name="Offline" />
          <Line type="monotone" dataKey="late" stroke="#f43f5e" strokeWidth={2} dot={{ r: 3, fill: "#f43f5e" }} name="Late Login" />
          <Line type="monotone" dataKey="hours" stroke="#eab308" strokeWidth={2} strokeDasharray="3 3" dot={false} name="Hours Worked" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </div>

  {/* Client Revenue Share (Pie Chart) */}
  <div className="glass-card p-5 flex flex-col justify-between">
    <div>
      <h2 className="text-lg font-semibold text-slate-800">Client Revenue Share</h2>
      <p className="text-xs text-slate-500 mt-1">Fee collections share from joined closures</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
      <div className="h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={clientRevenue.filter((x) => x.value > 0)}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={85}
              paddingAngle={3}
            >
              {["#4f46e5", "#06b6d4", "#8b5cf6", "#ec4899", "#f97316", "#cbd5e1"].map((color, index) => (
                <Cell key={index} fill={color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => [`₹${Number(value).toLocaleString("en-IN")}`, "Revenue"]}
              contentStyle={{ borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.05)" }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="space-y-2">
        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Top Business Sources</h4>
        {clientRevenue.length > 0 ? (
          clientRevenue.map((item, idx) => {
            const colors = ["#4f46e5", "#06b6d4", "#8b5cf6", "#ec4899", "#f97316", "#cbd5e1"];
            return (
              <div key={idx} className="flex items-center justify-between text-xs border-b border-slate-100 pb-1.5 last:border-0 last:pb-0">
                <div className="flex items-center gap-2 truncate pr-2">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: colors[idx % colors.length] }} />
                  <span className="font-semibold text-slate-700 truncate">{item.name}</span>
                </div>
                <span className="font-bold text-slate-900 shrink-0">₹{Number(item.value).toLocaleString("en-IN")}</span>
              </div>
            );
          })
        ) : (
          <p className="text-xs text-slate-400">No closure data found</p>
        )}
      </div>
    </div>
  </div>

</div>

{/* ================================================= */}
{/* ROW 2: LEADERBOARD & RECRUITMENT PIPELINE */}
{/* ================================================= */}
<div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">

  {/* Recruiter Performance Leaderboard */}
  <div className="glass-card p-5 flex flex-col justify-between">
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Top Recruiter Leaderboard</h2>
          <p className="text-xs text-slate-500 mt-1">Performance by total revenue generated</p>
        </div>
        <button
          onClick={() => navigate("/recruiter-report")}
          className="bg-indigo-600 text-white px-3.5 py-1.5 rounded-xl text-xs font-semibold hover:bg-indigo-755 transition-colors shadow-sm"
        >
          Leaderboard
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-slate-400 font-bold border-b border-slate-100 text-left">
              <th className="pb-2">Recruiter</th>
              <th className="pb-2 text-center">Closures</th>
              <th className="pb-2 text-right">Revenue Generated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {recruiterLeaders.length > 0 ? (
              recruiterLeaders.map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                  <td className="py-3 flex items-center gap-2.5">
                    <span className="w-6 h-6 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center font-bold text-[10px] text-indigo-600">
                      {idx + 1}
                    </span>
                    <span className="font-semibold text-slate-700">{item.name}</span>
                  </td>
                  <td className="py-3 text-center font-bold text-slate-800">{item.closures}</td>
                  <td className="py-3 text-right">
                    <span className="font-bold text-emerald-600">₹{Number(item.revenue).toLocaleString("en-IN")}</span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="text-center py-6 text-slate-400">No recruiter data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>

  {/* Current Month Position Matrix (Donut Chart) */}
  <div className="glass-card p-5 flex flex-col justify-between">
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Recruitment Pipeline</h2>
          <p className="text-xs text-slate-500 mt-1">Current month stage conversion metrics</p>
        </div>
        <button
          onClick={() => navigate("/matrix")}
          className="bg-indigo-600 text-white px-3.5 py-1.5 rounded-xl text-xs font-semibold hover:bg-indigo-755 transition-colors shadow-sm"
        >
          Position Matrix
        </button>
      </div>

      {(() => {
        const pipelineData = [
          { name: "Received", value: currentMonthMatrix.reduce((sum, item) => sum + Number(item["Received"] || item.received || 0), 0) },
          { name: "Screening", value: currentMonthMatrix.reduce((sum, item) => sum + Number(item["Screening"] || item.screening || 0), 0) },
          { name: "Shortlisted", value: currentMonthMatrix.reduce((sum, item) => sum + Number(item["Shortlisted"] || item.shortlisted || 0), 0) },
          { name: "Interview L1", value: currentMonthMatrix.reduce((sum, item) => sum + Number(item["Interview L1"] || item.interview_l1 || 0), 0) },
          { name: "Interview L2", value: currentMonthMatrix.reduce((sum, item) => sum + Number(item["Interview L2"] || item.interview_l2 || 0), 0) },
          { name: "Face to Face", value: currentMonthMatrix.reduce((sum, item) => sum + Number(item["Face to Face"] || item.face_to_face || 0), 0) },
          { name: "Selected", value: currentMonthMatrix.reduce((sum, item) => sum + Number(item["Selected"] || item.selected || 0), 0) },
          { name: "Offered", value: currentMonthMatrix.reduce((sum, item) => sum + Number(item["Offered"] || item.offered || 0), 0) },
          { name: "Joining Pending", value: currentMonthMatrix.reduce((sum, item) => sum + Number(item["Joining Pending"] || item.joining_pending || 0), 0) },
          { name: "Joined", value: dashboard.joinedCount || 0 },
          { name: "Rejected", value: currentMonthMatrix.reduce((sum, item) => sum + Number(item["Rejected"] || item.rejected || 0), 0) },
        ];

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pipelineData.filter(x => x.value > 0)}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {["#6366f1", "#10b981", "#8b5cf6", "#ec4899", "#f97316", "#0ea5e9", "#3b82f6", "#14b8a6", "#f43f5e", "#eab308", "#cbd5e1"].map((color, index) => (
                      <Cell key={index} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.05)" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="max-h-[220px] overflow-y-auto pr-2 space-y-1.5 scrollbar-thin">
              {pipelineData.map((item, idx) => {
                const colors = ["#6366f1", "#10b981", "#8b5cf6", "#ec4899", "#f97316", "#0ea5e9", "#3b82f6", "#14b8a6", "#f43f5e", "#eab308", "#cbd5e1"];
                return (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 truncate">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: colors[idx % colors.length] }} />
                      <span className="text-slate-600 truncate">{item.name}</span>
                    </div>
                    <span className="font-bold text-slate-800">{item.value}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  </div>

</div>

{/* ================================================= */}
{/* ROW 3: YEARLY CLOSURE & INTERVIEW FLOW */}
{/* ================================================= */}
<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

  {/* Yearly Closure Analytics */}
  <div className="glass-card p-5 flex flex-col justify-between">
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Yearly Closure Analytics</h2>
          <p className="text-xs text-slate-500 mt-1">{currentYear} monthly closures & active positions</p>
        </div>
        <button
          onClick={() => navigate("/closure-report")}
          className="bg-indigo-600 text-white px-3.5 py-1.5 rounded-xl text-xs font-semibold hover:bg-indigo-755 transition-colors shadow-sm"
        >
          View Closures
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100/60 shadow-xs">
          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Total Closures</p>
          <h2 className="text-xl font-bold mt-1 text-indigo-600">
            {recruiterPerformance.reduce((sum, item) => sum + item.closures, 0)}
          </h2>
        </div>
        <div className="bg-slate-50/50 rounded-2xl p-3 border border-slate-100/60 shadow-xs">
          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Active Jobs</p>
          <h2 className="text-xl font-bold mt-1 text-slate-700">
            {recruiterPerformance.reduce((sum, item) => sum + item.activePositions, 0)}
          </h2>
        </div>
      </div>
    </div>

    <div className="h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={recruiterPerformance} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
          <CartesianGrid stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} />
          <Tooltip
            contentStyle={{ borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.05)" }}
          />
          <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "10px" }} />
          <Bar dataKey="activePositions" fill="#a5b4fc" radius={[4, 4, 0, 0]} barSize={16} name="Active Positions" />
          <Line type="monotone" dataKey="closures" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 3, fill: "#4f46e5" }} name="Closures" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </div>

  {/* Interview • Joining • Closure Flow */}
  <div className="glass-card p-5 flex flex-col justify-between">
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Monthly Recruitment Flow</h2>
          <p className="text-xs text-slate-500 mt-1">Conversion pipeline flow metrics</p>
        </div>
        <button
          onClick={() => navigate("/interview-report")}
          className="bg-indigo-600 text-white px-3.5 py-1.5 rounded-xl text-xs font-semibold hover:bg-indigo-755 transition-colors shadow-sm"
        >
          View Interviews
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-4 text-center">
        <div className="bg-slate-50/50 rounded-2xl p-2.5 border border-slate-100/60 shadow-xs">
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">L1 Int</p>
          <h2 className="text-base font-bold mt-1 text-slate-800">{dashboard.interviewL1}</h2>
        </div>
        <div className="bg-slate-50/50 rounded-2xl p-2.5 border border-slate-100/60 shadow-xs">
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">L2 Int</p>
          <h2 className="text-base font-bold mt-1 text-slate-800">{dashboard.interviewL2}</h2>
        </div>
        <div className="bg-slate-50/50 rounded-2xl p-2.5 border border-slate-100/60 shadow-xs">
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">F2F</p>
          <h2 className="text-base font-bold mt-1 text-slate-800">{dashboard.faceToFace}</h2>
        </div>
        <div className="bg-slate-50/50 rounded-2xl p-2.5 border border-slate-100/60 shadow-xs">
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">Joined</p>
          <h2 className="text-base font-bold mt-1 text-emerald-600">{dashboard.joinedCount}</h2>
        </div>
      </div>
    </div>

    <div className="h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={hiringFlow} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
          <CartesianGrid stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
          <Tooltip
            contentStyle={{ borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.05)" }}
          />
          <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "10px" }} />
          <Line type="monotone" dataKey="interview" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3, fill: "#6366f1" }} name="Interview" />
          <Line type="monotone" dataKey="joining" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3, fill: "#10b981" }} name="Joining" />
          <Line type="monotone" dataKey="closure" stroke="#ec4899" strokeWidth={2.5} dot={{ r: 3, fill: "#ec4899" }} name="Closure" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>

</div>

{/* ROW 4: MONTHLY HIRING TREND */}
{/* ================================================= */}
<div className="glass-card p-5 mb-6">
  <div className="mb-4">
    <h2 className="text-lg font-semibold text-slate-800">Monthly Hiring Trend</h2>
    <p className="text-xs text-slate-500 mt-1">Growth and scaling analytics</p>
  </div>

  <div className="h-[260px]">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={monthlyHiring} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
        <CartesianGrid stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 11 }} />
        <YAxis axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
        <Tooltip
          contentStyle={{ borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.05)" }}
        />
        <Area type="monotone" dataKey="hiring" stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.08} strokeWidth={2.5} name="Hiring Count" />
      </AreaChart>
    </ResponsiveContainer>
  </div>
</div>
    </div>
  );
}